/* eslint-disable no-unused-vars */ 

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useQuery } from "react-query";
import {
  I2cTab,
  I2cTabGroup,
  I2cInput,
} from "@webcomponents/i2cwebcomponents/dist/react";
import DataTable from "../../Components/DataTable/DataTable";
import UserManagmentService from "../../Services/UsermanagmentService";
import "./SalesRequest.scss";

const AssignProjectUsers = ({
  closeModal = () => null,
  userTableDataApi,
  userTableDataApiExternal,
  selectedUsers = [],
  onSelectedUsersChange = () => null,
}) => {
  const [page, setPage] = useState(1);
  const [usersType, setUsersType] = useState("internal");
  const [internalUsers, setInternalUsers] = useState([]);
  const [externalUsers, setExternalUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("internal");
  const [searchQuery, setSearchQuery] = useState("");

  // Refs so selection handlers held by AG Grid (which can be stale closures
  // captured on first render of DataTable) always see the latest values.
  const selectedUsersRef = useRef(selectedUsers);
  const internalUsersRef = useRef(internalUsers);
  const externalUsersRef = useRef(externalUsers);
  selectedUsersRef.current = selectedUsers;
  internalUsersRef.current = internalUsers;
  externalUsersRef.current = externalUsers;

  const {
    data,
    error,
    isLoading,
    refetch: refetchUsers,
  } = useQuery(
    ["getProjectUsers"],
    () => UserManagmentService.getProjectUsers(),
    {
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    if (data && data.data && Array.isArray(data.data.data)) {
      const allUsers = data.data.data.filter(
        (user) => user.role !== "viewonly"
      );
      setInternalUsers(allUsers.filter((user) => user.role !== "client"));
      setExternalUsers(allUsers.filter((user) => user.role === "client"));
    } else {
      setInternalUsers([]);
      setExternalUsers([]);
    }
  }, [data]);
  const columnDefs = [
    {
      field: "",
      headerName: " ",
      checkboxSelection: true,
      showDisabledCheckboxes: true,
      flex: 0,
      minWidth: 64,
      maxWidth: 64,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center", padding: "0 8px" },
      suppressMenu: true,
    },
    {
      field: "name",
      headerName: "Name",
      sortable: true,
      flex: 1,
      minWidth: 178,
      suppressMenu: true,
    },
    {
      field: "email",
      headerName: "Email",
      sortable: true,
      flex: 2,
      minWidth: 220,
      suppressMenu: true,
      cellStyle: { overflow: "visible", whiteSpace: "normal" },
    },
    {
      field: "designation",
      headerName: "Designation",
      sortable: true,
      flex: 1,
      minWidth: 178,
      suppressMenu: true,
    },
  ];

  const noData = [
    {
      heading: "No records found",
      description: "No records were found.",
      type: "noData",
      icon: "outline-info-circle",
      iconColor: "var(--i2c-color-primary-600)",
    },
  ];

  const selectedUserIds = selectedUsers.map((user) => String(user.user_id));

  const mergeTabSelection = (tabType, selectedRows) => {
    // Always read latest values via refs to avoid stale-closure issues when
    // AG Grid keeps a reference to an older onSelectionChange handler.
    const latestSelectedUsers = selectedUsersRef.current;
    const tabUsers =
      tabType === "internal"
        ? internalUsersRef.current
        : externalUsersRef.current;

    // IDs that belong to the tab whose selection just changed
    const tabUserIds = new Set(tabUsers.map((u) => String(u.user_id)));

    // Preserve selections from the OTHER tab (their IDs aren't in tabUserIds)
    const usersFromOtherTab = latestSelectedUsers.filter(
      (user) => !tabUserIds.has(String(user.user_id))
    );

    // Enforce single selection per tab: AG Grid checkboxes can bypass
    // rowSelection:"single" and report multiple rows. Take only the newest
    // checked row (last in the array) so state stays at 1 per tab, which
    // then triggers restoreSelectedRows to visually uncheck the extras.
    const singleRow = selectedRows.length > 0
      ? [selectedRows[selectedRows.length - 1]]
      : [];

    const selectedUserMap = new Map();
    usersFromOtherTab.forEach((user) => {
      selectedUserMap.set(String(user.user_id), user);
    });
    singleRow.forEach((user) => {
      selectedUserMap.set(String(user.user_id), user);
    });

    onSelectedUsersChange(Array.from(selectedUserMap.values()));
  };

  const handleInternalSelectionChange = ({ selectedRows }) => {
    mergeTabSelection("internal", selectedRows);
  };

  const handleExternalSelectionChange = ({ selectedRows }) => {
    mergeTabSelection("external", selectedRows);
  };

  const filterUsers = (users) => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.designation?.toLowerCase().includes(q)
    );
  };

  const filteredInternalUsers = useMemo(
    () => filterUsers(internalUsers),
    [internalUsers, searchQuery],
  );

  const filteredExternalUsers = useMemo(
    () => filterUsers(externalUsers),
    [externalUsers, searchQuery],
  );

  const handleTabChange = (tabType) => {
    setActiveTab(tabType);
    setUsersType(tabType);
  };

  return (
    <React.Fragment>
      <div className="dTable_assignUsers">
        <div className="assign-users-header">
          <I2cTabGroup size="small" primary className="user_dialog_tab">
            <I2cTab
              slot="nav"
              panel="internal"
              onClick={() => handleTabChange("internal")}
            >
              Internal Owner
            </I2cTab>
            <I2cTab
              slot="nav"
              panel="external"
              onClick={() => handleTabChange("external")}
            >
              Client
            </I2cTab>
          </I2cTabGroup>
          <I2cInput
            placeholder="Search..."
            size="small"
            value={searchQuery}
            onI2cInput={(e) => setSearchQuery(e.target.value)}
            clearable
            style={{ minWidth: "200px" }}
          />
        </div>
        {activeTab === "internal" && (
          <DataTable
            key="internal-users-table"
            columnDefs={columnDefs}
            rowData={filteredInternalUsers}
            title=""
            noData={noData}
            pageSize={10}
            currentPage={page}
            setCurrentPage={setPage}
            total={filteredInternalUsers.length}
            tableDataApi={userTableDataApi}
            description=""
            closeModal={closeModal}
            rowSelection="single"
            getRowId={(row) => row.user_id}
            selectedRowIds={selectedUserIds}
            onSelectionChange={handleInternalSelectionChange}
          />
        )}
        {activeTab === "external" && (
          <DataTable
            key="external-users-table"
            columnDefs={columnDefs}
            rowData={filteredExternalUsers}
            title=""
            noData={noData}
            pageSize={10}
            currentPage={page}
            setCurrentPage={setPage}
            total={filteredExternalUsers.length}
            description=""
            tableDataApi={userTableDataApiExternal}
            closeModal={closeModal}
            rowSelection="single"
            getRowId={(row) => row.user_id}
            selectedRowIds={selectedUserIds}
            onSelectionChange={handleExternalSelectionChange}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default AssignProjectUsers;
